// context.ts — Shared focus + hover state for the one continuous galaxy map.
// Focus is a hierarchy — galaxy (nothing focused) → a star's system → a planet or a
// point of singularity within it — but it only drives the camera and the HUD;
// nothing in the scene is repositioned. Clicking flies the camera to that entity.
//
// There is ONE focus slot: `focusedId`, the id of the deepest focused entity (any
// kind). The typed views below (focusedSystemId / focusedPlanetId /
// selectedSingularityId) are DERIVED from it (see state/focus.ts) so the renderer
// keeps its convenient by-kind reads while the state has a single source of truth.
import { createContext, useContext } from 'react';
import type { Command, SimState } from './economy';

export interface GameState {
  /** The single focused-entity id (system, planet, or singularity), or null = galaxy. */
  focusedId: string | null;
  // Derived views of focusedId (resolveFocus), for convenient by-kind reads:
  focusedSystemId: string | null;
  /** The focused planet (or a focused moon's parent planet), or null. */
  focusedPlanetId: string | null;
  /** The focused moon within the focused planet, or null. */
  focusedMoonId: string | null;
  /** The focused hyperlane gate (point of singularity), or null. Id is `${sys}>${dst}`. */
  selectedSingularityId: string | null;
  hoveredId: string | null;
  /** The selected ship (id), or null. Ships are sim entities, not galaxy entities, so
   *  selection is a slot of its own — independent of the focusedId hierarchy. */
  selectedShipId: string | null;
  /** Select a ship (opens its info pane), or null to deselect. If systemId is given,
   *  also enter that system (so an in-system ship clicked from the galaxy view flies in). */
  selectShip: (id: string | null, systemId?: string) => void;
  /** Simulation speed multiplier (1× = real-time); see gameClock.ts. */
  speed: number;
  /** Focus any entity by its id (a system, planet, or singularity). null = galaxy. */
  focus: (id: string | null) => void;
  /** Step out one level: a planet/gate returns to its system; a system to the galaxy. */
  focusUp: () => void;
  hover: (id: string | null) => void;
  /** Set the simulation speed multiplier. */
  setSpeed: (speed: number) => void;
}

export const GameContext = createContext<GameState | null>(null);

export function useGame(): GameState {
  const ctx = useContext(GameContext);
  if (!ctx) {
    throw new Error('useGame must be used within a <GameContext.Provider>');
  }
  return ctx;
}

// The authoritative economy snapshot lives in its OWN context, separate from the
// focus/hover GameState above. It changes on every server snapshot (~4 Hz), so only
// the few components that render ships/economy subscribe to it — keeping the ~220
// always-on StarMarkers (which only read GameState) from re-rendering every snapshot.
export const SimContext = createContext<SimState | null>(null);

/** The latest server economy snapshot, or null before the first snapshot / offline. */
export function useSim(): SimState | null {
  return useContext(SimContext);
}

// MatchContext is present only while playing a match (null in the sandbox galaxy): who you
// are, whether you host (and so set the pace), and the order channel + fleet selection
// the HUD and scene share.
export interface MatchState {
  matchId: string;
  name: string;
  /** Your empire id ("" = spectating). */
  you: string;
  isHost: boolean;
  /** Game-seconds per wall-second at "1×". */
  baseSpeed: number;
  /** Issue an order; resolves to an error message or null on success. */
  issue: (cmd: Command) => Promise<string | null>;
  /** Your ships currently selected for orders. */
  selection: string[];
  setSelection: (ids: string[]) => void;
}

export const MatchContext = createContext<MatchState | null>(null);

export function useMatch(): MatchState | null {
  return useContext(MatchContext);
}
